import {
  Box,
  Checkbox,
  Grid,
  TableHead,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { visuallyHidden } from '@mui/utils';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import AlertCircle from '../../../../components/AlertCircle';
import NoData from '../../../../components/NoData';
import { calculateComplianceFill } from '../../../../helpers/calculateFill/calculateFill';
import { useClientTheme } from '../../../../helpers/hooks/useClientTheme';
import { CompliancePeriod } from '../../../../model/compliance/CompliancePeriod';
import { updatePatientCompliancePeriod } from '../../../../redux/actions/complianceActions';
import { useAppThunkDispatch } from '../../../../redux/configureStore';
import { compliancePeriods } from '../../../../redux/selectors/complianceSelectors';
import {
  getComparator,
  Order,
  StyledTableCell,
} from '../../TableComponents/TableComponents';

interface HeadCell {
  id: keyof CompliancePeriod;
  label: string;
  numeric: boolean;
  align: 'left' | 'right' | 'inherit' | 'center' | 'justify' | undefined;
  toolTip?: string;
}

interface ComplianceDetailTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof CompliancePeriod
  ) => void;
  order: Order;
  orderBy: string;
}

function formatDateRange(startTime: string, endTime: string): string {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  const startDate = new Date(startTime).toLocaleDateString('en-US', options);
  const endDate = new Date(endTime).toLocaleDateString('en-US', options);
  return `${startDate} - ${endDate}`;
}

function ComplianceDetailTableHead(props: ComplianceDetailTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const { t } = useTranslation(['compliance', 'common']);
  const createSortHandler =
    (property: keyof CompliancePeriod) =>
    (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const headCells: readonly HeadCell[] = [
    {
      id: 'startTime',
      numeric: false,
      label: t('table.period'),
      align: 'left',
    },

    {
      id: 'daysOfUse',
      numeric: true,
      label: t('table.daysOfUse'),
      align: 'center',
    },
    {
      id: 'isBilled',
      numeric: false,
      label: t('table.wasBilled'),
      align: 'center',
    },
  ];

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => {
          return (
            <StyledTableCell
              key={headCell.id}
              align={headCell.align}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              <Tooltip title={headCell.toolTip || ''}>
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc'
                        ? t('common:table.sortedDescending')
                        : t('common:table.sortedAscending')}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </Tooltip>
            </StyledTableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

const ComplianceDetailTableComponent: React.FC = () => {
  const { t } = useTranslation('compliance');
  const data = useSelector(compliancePeriods);
  const { theme } = useClientTheme();
  const dispatch = useAppThunkDispatch();
  //Not sure if this needs to be put into config?
  const daysOfUseEnd = 30;
  const requiredDaysOfUse = 16;
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] =
    React.useState<keyof CompliancePeriod>('isCurrent');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof CompliancePeriod
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleCheckboxChange = (id: number, newValue: boolean) => {
    const updatedItem = data.find((item: CompliancePeriod) => item.id === id);
    if (updatedItem) {
      const updatedItemWithNewValue = {
        ...updatedItem,
        isBilled: newValue,
      };
      dispatch(updatePatientCompliancePeriod(updatedItemWithNewValue));
    }
  };

  function getBillingCheckboxProps(period: CompliancePeriod): {
    isDisabled: boolean;
    tooltipTitle: string;
  } {
    const isDisabled =
      period.isCurrent || period.daysOfUse <= requiredDaysOfUse - 1;
    const tooltipTitle = period.isCurrent
      ? t('table.billingDisabledCurrent')
      : period.daysOfUse <= requiredDaysOfUse - 1
        ? t('table.billingDisabledInsufficient')
        : '';

    return { isDisabled, tooltipTitle };
  }

  function renderComplianceTooltip(fill?: string) {
    const complianceCircles = [
      {
        fill: theme.palette.complianceNotAchievable.main,
        text: t('tooltip.notAchievable'),
      },
      {
        fill: theme.palette.complianceAtRisk.main,
        text: t('tooltip.atRisk'),
      },
      {
        fill: theme.palette.complianceOnTrack.main,
        text: t('tooltip.onTrack'),
      },
      {
        fill: theme.palette.complianceCompleted.main,
        text: t('tooltip.completed'),
      },
    ];

    if (fill) {
      const circle = complianceCircles.find((circle) => circle.fill === fill);
      return circle ? circle.text : '';
    }

    return (
      <Grid container>
        <Grid size={12} sx={{ mt: 0.5 }}>
          {t('tooltip.intro')}
        </Grid>
        {complianceCircles.map((circle, index) => (
          <React.Fragment key={index}>
            <Grid size={3}


              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '5px 0',
              }}
            >
              <AlertCircle fill={circle.fill} />
            </Grid>
            <Grid size={7} style={{ margin: '5px 0' }}>
              {circle.text}
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
    );
  }

  return data.length ? (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="compliance-data-table">
            <ComplianceDetailTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {data
                .slice()
                .sort(getComparator(order, orderBy))
                .map((period: CompliancePeriod) => {
                  const { isDisabled, tooltipTitle } =
                    getBillingCheckboxProps(period);
                  return (
                    <TableRow tabIndex={-1} key={period.id}>
                      <StyledTableCell align="left">
                        {formatDateRange(period.startTime, period.endTime)}
                      </StyledTableCell>
                      <StyledTableCell style={{ width: '300px' }} align="left">
                        <Grid container>
                          <Grid size={3}


                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            {!period.isCurrent ? (
                              <Tooltip
                                title={renderComplianceTooltip(
                                  calculateComplianceFill(
                                    theme,
                                    period.daysOfUse,
                                    daysOfUseEnd
                                  )
                                )}
                              >
                                <AlertCircle
                                  data-testid="compliance-circle"
                                  fill={calculateComplianceFill(
                                    theme,
                                    period.daysOfUse,
                                    daysOfUseEnd
                                  )}
                                />
                              </Tooltip>
                            ) : null}
                          </Grid>

                          <Grid size={7}>
                            {t('table.daysOfUseValue', {
                              days: period.daysOfUse,
                              total: daysOfUseEnd,
                            })}
                          </Grid>
                        </Grid>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Tooltip title={tooltipTitle}>
                          <span>
                            <Checkbox
                              color="primary"
                              checked={period.isBilled}
                              disabled={isDisabled}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  period.id,
                                  e.target.checked
                                )
                              }
                              data-testid={`checkbox-isbilled-${period.id}`}
                            />
                          </span>
                        </Tooltip>
                      </StyledTableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  ) : (
    <NoData message={t('table.noData')} />
  );
};

export default ComplianceDetailTableComponent;
